import { useLayoutEffect, useRef } from 'react';
import { cn } from '@/lib/format';

/**
 * One line of text that shrinks until it fits its container.
 *
 * Stat tiles have to hold both "6" and "Rp 222.500.000" in the same box, and a
 * single fixed font size cannot serve both: pick one that fits the rupiah value
 * and the counts look timid, pick one that suits the counts and the rupiah
 * value runs past the edge. What happened instead was worse than either —
 * `truncate` turned it into "Rp 222...." and `.card` being overflow-hidden cut
 * the rest off silently, so the figure the page exists to show was the one part
 * the user could not read.
 *
 * So the size is measured, not guessed. The text renders at the size its class
 * asks for; if that overflows, the font is scaled down by exactly the ratio it
 * overflowed by, to a floor. Short values keep the full size, long ones get
 * smaller — never clipped, never ellipsised, never wrapped mid-number.
 *
 * `minFontPx` is a readability floor, not a fit guarantee: a value long enough
 * to overflow even at the floor is left overflowing rather than shrunk into
 * illegibility. Nothing in this app gets close — 'Rp 999.999.999.999' fits a
 * 160px tile at 15px — but if something does, an overflowing number is a
 * visible bug, while a 6px one looks like a design choice and hides.
 */
export function FitText({
  children,
  className,
  minFontPx = 14,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  /** Never shrink below this. Default 14px — the smallest comfortably legible. */
  minFontPx?: number;
  /** Tooltip. Defaults to the text itself when it is a plain string. */
  title?: string;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const text = textRef.current;
    if (!box || !text) return;

    let cancelled = false;

    const fit = () => {
      if (cancelled) return;
      const available = box.clientWidth;
      if (available === 0) return; // hidden or not laid out yet

      // Always measure from the class-given size. Measuring from whatever we
      // set last time would compound: each pass would shrink the already
      // shrunken text and the number would creep smaller on every resize.
      text.style.fontSize = '';
      const natural = text.scrollWidth;
      if (natural === 0 || natural <= available) return;

      const base = parseFloat(window.getComputedStyle(text).fontSize) || 16;
      text.style.fontSize = `${Math.max(minFontPx, (base * available) / natural)}px`;
    };

    fit();

    // Width only. Shrinking the font changes the line height, which resizes the
    // box, which would call this back — and that pass resets to the natural
    // size before measuring, so the two sizes would alternate forever. Watching
    // the one dimension that actually decides the fit breaks the cycle.
    let lastWidth = box.clientWidth;
    const ro = new ResizeObserver(() => {
      const width = box.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      fit();
    });
    ro.observe(box);

    // Web fonts land after first paint and are wider than the fallback, so a
    // value measured against the fallback can overflow once the real font
    // arrives.
    void document.fonts?.ready.then(fit);

    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, [children, minFontPx]);

  return (
    <div ref={boxRef} className="min-w-0">
      <span
        ref={textRef}
        className={cn('inline-block whitespace-nowrap', className)}
        title={title ?? (typeof children === 'string' ? children : undefined)}
      >
        {children}
      </span>
    </div>
  );
}
